import React, { useState } from "react";
import { Reveal, Eyebrow } from "../components/ui";
import { API_BASE_URL, ENDPOINTS } from "../config/api";

type Status = "idle" | "submitting" | "success" | "error";
type Phase = "request" | "verify";

const removed = [
  "Your profile, photos, and bio",
  "Your matches and messages",
  "Your personal data",
];

const steps = [
  "Enter the email linked to your account",
  "Enter the 6-digit code we email you",
  "Confirm, and your account is scheduled for deletion",
];

export default function DeleteAccount() {
  const [phase, setPhase] = useState<Phase>("request");

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const [status, setStatus] = useState<Status>("idle");
  const [infoMessage, setInfoMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const isValidEmail = /\S+@\S+\.\S+/.test(email);
  const isValidCode = /^\d{6}$/.test(code);

  const canRequest = isValidEmail && confirmed && status !== "submitting";

  const canVerify = isValidEmail && isValidCode && status !== "submitting";

  function extractBackendMessage(json: any, fallback: string) {
    if (!json) return fallback;
    if (typeof json.message === "string") return json.message;
    if (Array.isArray(json.message)) return json.message.join(", ");
    if (typeof json.error === "string") return json.error;
    return fallback;
  }

  // STEP 1 — ask the backend to email a 6-digit code
  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!canRequest) return;

    setStatus("submitting");
    setErrorMessage("");
    setInfoMessage("");

    try {
      const res = await fetch(
        `${API_BASE_URL}${ENDPOINTS.ACCOUNT_DELETE_REQUEST_CODE}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        },
      );

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        setErrorMessage(
          extractBackendMessage(json, `Request failed (status ${res.status}).`),
        );
        setStatus("error");
        return;
      }

      setInfoMessage(
        `We've emailed a 6-digit code to ${email}. It expires in a few minutes.`,
      );
      setStatus("idle");
      setPhase("verify");
    } catch {
      setErrorMessage("Could not reach the server. Please try again.");
      setStatus("error");
    }
  }

  // STEP 2 — submit email + code to actually delete
  async function handleConfirmDelete(e: React.FormEvent) {
    e.preventDefault();
    if (!canVerify) return;

    setStatus("submitting");
    setErrorMessage("");
    setInfoMessage("");

    try {
      const res = await fetch(
        `${API_BASE_URL}${ENDPOINTS.ACCOUNT_DELETE_REQUEST}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, code }),
        },
      );

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        setErrorMessage(
          extractBackendMessage(json, `Request failed (status ${res.status}).`),
        );
        setStatus("error");
        return;
      }

      // Clear any local session — this user is now soft-deleted.
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");

      setStatus("success");
    } catch {
      setErrorMessage("Could not reach the server. Please try again.");
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <main className="bg-white px-[5%] pt-[140px] pb-20 min-h-[60vh] flex items-start">
        <div className="max-w-[520px] mx-auto text-center">
          <Reveal>
            <div className="w-14 h-14 rounded-full bg-rose/10 flex items-center justify-center mx-auto mb-6">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="#c45a7e"
                strokeWidth="2"
                className="w-6 h-6"
              >
                <polyline points="20,6 9,17 4,12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold tracking-tight mb-3">
              Account scheduled for deletion
            </h1>
            <p className="text-[15px] text-muted leading-relaxed">
              The account linked to <strong>{email}</strong> has been
              deactivated. Log back in before the scheduled date to cancel the
              deletion.
            </p>
          </Reveal>
        </div>
      </main>
    );
  }

  const currentStep = phase === "request" ? 0 : 1;

  return (
    <main className="bg-white px-[5%] pt-[140px] pb-20 lg:pb-[100px]">
      <div className="max-w-[1000px] mx-auto">
        <Reveal>
          <Eyebrow>Account</Eyebrow>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="text-[clamp(26px,4vw,38px)] font-bold tracking-[-1px] mb-3">
            Delete your account
          </h1>
        </Reveal>
        <Reveal delay={150}>
          <p className="text-[15px] text-muted leading-relaxed mb-10 max-w-[560px]">
            This will permanently remove your profile, matches, messages, and
            personal data. This action cannot be undone.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* ---------- Info panel ---------- */}
          <Reveal delay={175} className="order-2 lg:order-1">
            <div className="p-6 lg:p-8 rounded-[24px] bg-blush border border-rose/10">
              <h2 className="text-lg font-bold tracking-tight mb-3">
                What will be removed
              </h2>
              <ul className="space-y-2 mb-8">
                {removed.map((r) => (
                  <li
                    key={r}
                    className="flex items-start gap-3 text-[14px] text-muted leading-relaxed"
                  >
                    <span className="text-rose mt-0.5">•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>

              <h2 className="text-lg font-bold tracking-tight mb-3">
                How it works
              </h2>
              <ol className="space-y-3 mb-8">
                {steps.map((s, i) => (
                  <li
                    key={s}
                    className={`flex items-start gap-3 text-[14px] leading-relaxed ${
                      i === currentStep
                        ? "text-black font-medium"
                        : "text-muted"
                    }`}
                  >
                    <span
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 ${
                        i === currentStep
                          ? "bg-black text-white"
                          : "bg-white border border-black/10 text-muted"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <span className="pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>

              <p className="text-[13px] text-muted leading-relaxed">
                Changed your mind? Log back in before the scheduled date to
                cancel the deletion.
              </p>
            </div>
          </Reveal>

          {/* ---------- Form card ---------- */}
          <div className="order-1 lg:order-2 p-6 lg:p-8 rounded-[24px] border border-black/10 bg-white">
            {infoMessage && (
              <div className="mb-6 p-4 rounded-2xl bg-blush border border-rose/20">
                <p className="text-[13px] text-rose-dark leading-relaxed">
                  {infoMessage}
                </p>
              </div>
            )}

            {/* ---------- PHASE 1: request code ---------- */}
            {phase === "request" && (
              <Reveal delay={200}>
                <form onSubmit={handleRequestCode} className="space-y-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-[13px] font-semibold text-black mb-2"
                    >
                      Account email
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm outline-none focus:border-rose transition-colors duration-200"
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-rose flex-shrink-0"
                    />
                    <span className="text-[13px] text-muted leading-relaxed">
                      I understand this will permanently delete my account and
                      all associated data.
                    </span>
                  </label>

                  {status === "error" && (
                    <p className="text-[13px] text-rose-dark">{errorMessage}</p>
                  )}

                  <button
                    type="submit"
                    disabled={!canRequest}
                    className="w-full bg-black text-white text-sm font-semibold py-3.5 rounded-full hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {status === "submitting"
                      ? "Sending code…"
                      : "Send verification code"}
                  </button>
                </form>
              </Reveal>
            )}

            {/* ---------- PHASE 2: enter code + delete ---------- */}
            {phase === "verify" && (
              <Reveal delay={200}>
                <form onSubmit={handleConfirmDelete} className="space-y-5">
                  <div>
                    <label
                      htmlFor="code"
                      className="block text-[13px] font-semibold text-black mb-2"
                    >
                      6-digit code
                    </label>
                    <input
                      id="code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      maxLength={6}
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      placeholder="847291"
                      className="w-full px-4 py-3.5 rounded-2xl border border-black/10 text-sm tracking-[0.4em] text-center outline-none focus:border-rose transition-colors duration-200"
                    />
                    <p className="text-[12px] text-muted mt-2">
                      Sent to <strong>{email}</strong>
                    </p>
                  </div>

                  {status === "error" && (
                    <p className="text-[13px] text-rose-dark">{errorMessage}</p>
                  )}

                  <button
                    type="submit"
                    disabled={!canVerify}
                    className="w-full bg-black text-white text-sm font-semibold py-3.5 rounded-full hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {status === "submitting" ? "Deleting…" : "Confirm deletion"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setPhase("request");
                      setCode("");
                      setErrorMessage("");
                      setInfoMessage("");
                      setStatus("idle");
                    }}
                    className="w-full text-[13px] text-muted hover:text-black transition-colors duration-200"
                  >
                    ← Use a different email
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
